// This is a style-agnostic + composable rewrite of https://www.npmjs.com/package/react-compare-slider
// It should be more flexible and controllable for our needs so that we
// aren't fighting against the library's constraints
//
// Copyright 2019 nerdyman, MIT License

import type {
  ClipperRefs,
  CompareSliderContextValue,
  Orientation,
} from "./compare-slider-exports";

import * as React from "react";

import isFunction from "lodash-es/isFunction";
import { parseInteger, round } from "replo-utils/lib/math";
import { useComposedRefs } from "replo-utils/react/use-composed-refs";
import { useControllableState } from "replo-utils/react/use-controllable-state";
import { useRect } from "replo-utils/react/use-rect";

import { getOwnerWindowSafe } from "../../../../shared/Window";
import {
  CompareSliderClipperContext,
  CompareSliderContext,
  useCompareSliderClipperContext,
  useInternalContext,
} from "./compare-slider-exports";

const keyboardEventKeys = [
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
] as const;

type KeyboardEventKey = (typeof keyboardEventKeys)[number];

type InteractionState = "idle" | "dragging";

const EVENT_PASSIVE_PARAMS = { capture: false, passive: true };
const EVENT_CAPTURE_PARAMS = { capture: true, passive: false };
const MAX_POSITION_PRECISION = 2;

// #region --- CompareSlider
interface CompareSliderOwnProps {
  isDisabled?: boolean;
  orientation?: Orientation;
  position?: number;
  defaultPosition?: number;
  /**
   * Shorthand CSS `transition` property to apply to handle movement. The specific CSS property
   * to transition **must not** be provided.
   * @example '.5s ease-in-out'
   */
  transition?: string;

  /** Padding in pixels to limit the slideable bounds on the slider's axis */
  boundsPadding?: number;
  /** Whether the slider should follow the pointer on hover. */
  changePositionOnHover?: boolean;
  /** Custom handle component. */
  handle: React.ReactNode;
  /** First item to show. */
  beforeElement: React.ReactNode;
  /** Second item to show. */
  afterElement: React.ReactNode;
  /** Percentage or pixel amount to move when the slider handle is focused and keyboard arrow is pressed. */
  keyboardIncrement?: number | `${number}%`;

  /** the element that will trigger drag events */
  dragTarget?: "root" | "handle";

  /** Callback on position change with position as percentage. */
  onPositionChange?: (position: number) => void;
  /** Window object for binding events. Defaults to `window`. */
  globalWindow?: Window | (Window & typeof globalThis);
}

interface CompareSliderProps
  extends CompareSliderOwnProps,
    Omit<React.ComponentPropsWithRef<"div">, keyof CompareSliderOwnProps> {}

export const CompareSlider = React.forwardRef<
  HTMLDivElement,
  CompareSliderProps
>(function CompareSlider(
  {
    boundsPadding = 0,
    globalWindow,
    changePositionOnHover = false,
    isDisabled = false,
    handle,
    beforeElement,
    afterElement,
    keyboardIncrement = "5%",
    dragTarget = "handle",
    onPositionChange,
    orientation = "horizontal",
    defaultPosition = 50,
    position: positionProp,
    style,
    transition,
    ...props
  },
  forwardedRef,
) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const clipperRefs = React.useRef<ClipperRefs>({
    before: null,
    after: null,
  });

  const handleRef = React.useRef<HTMLButtonElement>(null);
  // handle element is stateful, as we may need to detach/re-attach listeners
  // to sync w/ `dragTarget`
  const [handleElement, setHandleElement] =
    React.useState<HTMLButtonElement | null>(null);
  const [minSize, setMinSize] = React.useState(0);
  const [interactionState, setInteractionState] =
    React.useState<InteractionState>("idle");
  const isDragging = interactionState === "dragging";

  const [canTransition, setCanTransition] = React.useState(true);
  const hasBrowsingContextBinding = React.useRef(false);
  const isVertical = orientation === "vertical";

  const _positionProp =
    positionProp != null
      ? round(positionProp, MAX_POSITION_PRECISION)
      : undefined;
  const _defaultPosition = round(defaultPosition, MAX_POSITION_PRECISION);

  const [position, setPosition] = useControllableState(
    _positionProp,
    _defaultPosition,
    onPositionChange,
  );

  // track position in a ref we can read on demand in event handlers
  const internalPositionRef = useLiveRef(position);
  // TODO: maybe don't need this?
  const previousPosition = usePrevious(position);

  // NOTE (Chance 2024-06-06): Maintain a stateful root element to ensure the
  // DOM Rect is updated as soon as the ref is mounted
  const [rootElement, setRootElement] = React.useState<HTMLDivElement | null>(
    null,
  );
  const rect = useRect(rootElement, { observe: "size" });
  const rootHeight = rect?.height ?? 0;
  const rootWidth = rect?.width ?? 0;
  const rectIsDefined = Boolean(rect);

  /** Sync the internal position and trigger position change callback if defined. */
  const requestSetPosition = React.useCallback(
    function requestSetPosition({
      x,
      y,
      isOffset,
    }: {
      x: number;
      y: number;
      isOffset: boolean;
    }) {
      const rootElement = rootRef.current;
      if (!rootElement) {
        return;
      }

      const rect = rootElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      const window =
        globalWindow ??
        getOwnerWindowSafe(rootElement) ??
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        (globalThis as Window & typeof globalThis);

      let nextPosition = calculateSliderPosition({
        x,
        y,
        isOffset,
        orientation,
        rect,
        window,
      });
      nextPosition = round(nextPosition, MAX_POSITION_PRECISION);
      setPosition(nextPosition);
    },
    [globalWindow, orientation, setPosition],
  );

  const positionWithPadding = React.useMemo(() => {
    const offsetHeight = rootElement?.offsetHeight ?? 0;
    const offsetWidth = rootElement?.offsetWidth ?? 0;

    // Note (Noah, 2024-06-13): If there's no rect, then we're server-side rendering.
    // We want to just default to the position as specified, since we only support setting
    // a position via % which is always SSR-able. After hydration, we'll take padding of
    // the root bounds into account.
    if (!rectIsDefined) {
      return position;
    }

    return calculatePositionWithBoundsPadding(position, {
      height: rootHeight,
      width: rootWidth,
      boundsPadding,
      offsetHeight,
      offsetWidth,
      orientation,
    });
  }, [
    boundsPadding,
    orientation,
    position,
    rootElement,
    rootHeight,
    rootWidth,
    rectIsDefined,
  ]);

  // Update internal position when other user controllable props change.
  // TODO: This can be improved
  // eslint-disable-next-line replo/no-use-effect -- (Noah, 2026-07-20): Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const { width, height } = root.getBoundingClientRect();

    // Use current internal position if `position` hasn't changed.
    const nextPosition =
      position === previousPosition ? internalPositionRef.current : position;
    requestSetPosition({
      x: (width / 100) * nextPosition,
      y: (height / 100) * nextPosition,
      isOffset: false,
    });
  }, [
    boundsPadding,
    internalPositionRef,
    isVertical,
    position,
    previousPosition,
    requestSetPosition,
  ]);

  /** Handle mouse/touch move. */
  const handlePointerMove = React.useCallback(
    function moveCall(ev: PointerEvent) {
      requestSetPosition({ isOffset: true, x: ev.pageX, y: ev.pageY });
      setCanTransition(false);
    },
    [requestSetPosition],
  );

  /** Handle mouse/touch up. */
  const handlePointerUp = React.useCallback(() => {
    setInteractionState("idle");
    setCanTransition(true);
  }, []);

  // Handle hover events on the container.
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    const containerRef = rootRef.current as HTMLDivElement;
    function handlePointerLeave() {
      if (isDragging) {
        return;
      }
      handlePointerUp();
    }

    if (changePositionOnHover) {
      containerRef.addEventListener(
        "pointermove",
        handlePointerMove,
        EVENT_PASSIVE_PARAMS,
      );
      containerRef.addEventListener(
        "pointerleave",
        handlePointerLeave,
        EVENT_PASSIVE_PARAMS,
      );
    }

    return () => {
      containerRef.removeEventListener("pointermove", handlePointerMove);
      containerRef.removeEventListener("pointerleave", handlePointerLeave);
    };
  }, [changePositionOnHover, handlePointerMove, handlePointerUp, isDragging]);

  // Allow drag outside of container while pointer is still down.
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const rootElement = rootRef.current;
    const _window =
      globalWindow ??
      (rootElement ? getOwnerWindowSafe(rootElement) : null) ??
      globalThis;

    if (isDragging && !hasBrowsingContextBinding.current) {
      _window.addEventListener(
        "pointermove",
        handlePointerMove,
        EVENT_PASSIVE_PARAMS,
      );
      _window.addEventListener(
        "pointerup",
        handlePointerUp,
        EVENT_PASSIVE_PARAMS,
      );
      hasBrowsingContextBinding.current = true;
    }

    return () => {
      if (hasBrowsingContextBinding.current) {
        _window.removeEventListener("pointermove", handlePointerMove);
        _window.removeEventListener("pointerup", handlePointerUp);
        hasBrowsingContextBinding.current = false;
      }
    };
  }, [handlePointerMove, handlePointerUp, isDragging, globalWindow]);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const handleContainer = handleRef.current;
    if (!handleContainer) {
      return;
    }

    handleContainer.addEventListener(
      "click",
      handleContainerClick,
      EVENT_CAPTURE_PARAMS,
    );
    return () => {
      handleContainer.removeEventListener(
        "click",
        handleContainerClick,
        EVENT_CAPTURE_PARAMS,
      );
    };

    function handleContainerClick(event: MouseEvent) {
      event.preventDefault();
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      (event.currentTarget as HTMLButtonElement).focus();
    }
  }, []);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const interactiveTarget: HTMLElement | null =
      dragTarget === "handle" ? handleElement : rootRef.current;
    if (!interactiveTarget) {
      return;
    }

    interactiveTarget.addEventListener(
      "pointerdown",
      handlePointerDown,
      EVENT_CAPTURE_PARAMS,
    );
    return () => {
      interactiveTarget.removeEventListener(
        "pointerdown",
        handlePointerDown,
        EVENT_CAPTURE_PARAMS,
      );
    };

    function handlePointerDown(event: PointerEvent) {
      event.preventDefault();
      // Only handle left mouse button (touch events also use 0).
      if (isDisabled || event.button !== 0) {
        return;
      }

      requestSetPosition({
        isOffset: true,
        x: event.pageX,
        y: event.pageY,
      });
      setInteractionState("dragging");
      setCanTransition(true);
    }
  }, [dragTarget, handleElement, isDisabled, requestSetPosition]);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(
    function syncPositionOnResize() {
      const container = rootRef.current;
      if (!container) {
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        const contentRect = entry?.contentRect;
        if (!contentRect) {
          return;
        }
        const { width: scaledWidth, height: scaledHeight } =
          container.getBoundingClientRect();
        const internalPosition = internalPositionRef.current;
        requestSetPosition({
          x:
            ((contentRect.width / 100) * internalPosition * scaledWidth) /
            contentRect.width,
          y:
            ((contentRect.height / 100) * internalPosition * scaledHeight) /
            contentRect.height,
          isOffset: false,
        });
      });
      observer.observe(container);
      return () => {
        observer.disconnect();
      };
    },
    [internalPositionRef, requestSetPosition],
  );

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const handleContainer = handleRef.current;
    if (!handleContainer) {
      return;
    }

    handleContainer.addEventListener(
      "keydown",
      handleKeydown,
      EVENT_CAPTURE_PARAMS,
    );
    return () => {
      handleContainer.removeEventListener(
        "keydown",
        handleKeydown,
        EVENT_CAPTURE_PARAMS,
      );
    };

    function handleKeydown(event: KeyboardEvent) {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      if (!keyboardEventKeys.includes(event.key as KeyboardEventKey)) {
        return;
      }

      event.preventDefault();
      setCanTransition(true);

      const {
        top,
        left,
      } = // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        (handleRef.current as HTMLButtonElement).getBoundingClientRect();

      const {
        width,
        height,
      } = // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        (rootRef.current as HTMLDivElement).getBoundingClientRect();

      const isPercentage = typeof keyboardIncrement === "string";
      const incrementPercentage = isPercentage
        ? Number.parseFloat(keyboardIncrement)
        : (keyboardIncrement / width) * 100;

      const isIncrement = isVertical
        ? event.key === "ArrowLeft" || event.key === "ArrowDown"
        : event.key === "ArrowRight" || event.key === "ArrowUp";

      const nextPosition = Math.min(
        Math.max(
          isIncrement
            ? internalPositionRef.current + incrementPercentage
            : internalPositionRef.current - incrementPercentage,
          0,
        ),
        100,
      );

      requestSetPosition({
        x: isVertical ? left : (width * nextPosition) / 100,
        y: isVertical ? (height * nextPosition) / 100 : top,
        isOffset: false,
      });
    }
  }, [keyboardIncrement, isVertical, requestSetPosition, internalPositionRef]);

  // NOTE (Sebas, 2024-07-05):This effect ensures all clipper elements match
  // the height of the tallest one. This is necessary because the second clipper
  // is absolutely positioned and might be cut off if its content is taller than
  // the first clipper's content.
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const clipperElements = Object.values(clipperRefs.current);

    function captureLargestSize() {
      let largestSize = 0;

      for (const clipper of clipperElements) {
        if (!clipper?.firstChild) {
          continue;
        }
        const height = clipper.firstChild.clientHeight;
        largestSize = Math.max(largestSize, height);
      }

      setMinSize(largestSize);
    }

    const resizeObserver = new ResizeObserver(() => {
      captureLargestSize();
    });

    function setupResizeObservation() {
      captureLargestSize();

      resizeObserver.disconnect();
      for (const clipper of clipperElements) {
        if (clipper?.firstChild) {
          resizeObserver.observe(clipper.firstChild);
        }
      }
    }

    setupResizeObservation();

    const root = rootRef.current;
    if (!root) {
      return () => {
        resizeObserver.disconnect();
      };
    }

    // Watch for children being added/removed to handle late-mounting content
    const mutationObserver = new MutationObserver(() => {
      setupResizeObservation();
    });

    mutationObserver.observe(root, {
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  const ref = useComposedRefs(
    forwardedRef,
    rootRef,
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    setRootElement as React.RefCallback<HTMLDivElement>,
  );

  // Use custom handle if requested.
  const appliedTransition = canTransition ? transition : undefined;

  const getCursor = () => {
    if (isDragging || changePositionOnHover) {
      if (isVertical) {
        return "ns-resize";
      }
      return "ew-resize";
    }
    return undefined;
  };

  return (
    <div
      data-replo-component-root="compare-slider"
      data-replo-part="root"
      ref={ref}
      {...props}
      style={{
        position: "relative",
        display: "flex",
        overflow: "hidden",
        cursor: getCursor(),
        touchAction: "none",
        userSelect: "none",
        minHeight: style?.minHeight
          ? Math.max(minSize, parseInteger(style.minHeight))
          : minSize,
        ...style,
      }}
    >
      <CompareSliderContext.Provider
        value={{
          orientation,
          isDisabled,
          position,
          transition: appliedTransition,
          handleRef,
          clipperRefs,
          setHandleElement,
          isDragging,
          positionWithPadding,
        }}
      >
        <CompareSliderClipperContext.Provider value={{ position: "before" }}>
          {React.isValidElement(beforeElement) &&
          beforeElement.type === CompareSliderClipper ? (
            beforeElement
          ) : (
            <CompareSliderClipper>{beforeElement}</CompareSliderClipper>
          )}
        </CompareSliderClipperContext.Provider>

        <CompareSliderClipperContext.Provider value={{ position: "after" }}>
          {React.isValidElement(afterElement) &&
          afterElement.type === CompareSliderClipper ? (
            afterElement
          ) : (
            <CompareSliderClipper>{afterElement}</CompareSliderClipper>
          )}
        </CompareSliderClipperContext.Provider>

        {handle}
      </CompareSliderContext.Provider>
    </div>
  );
});
// #endregion

// #region --- CompareSliderHandle
interface CompareSliderHandleOwnProps {
  children?:
    | React.ReactNode
    | ((context: CompareSliderContextValue) => React.ReactNode);
}
interface CompareSliderHandleProps
  extends Omit<
      React.ComponentPropsWithRef<"button">,
      "type" | keyof CompareSliderHandleOwnProps
    >,
    CompareSliderHandleOwnProps {}

export const CompareSliderHandle = React.forwardRef<
  HTMLButtonElement,
  CompareSliderHandleProps
>(function CompareSliderHandle(
  {
    // @ts-expect-error
    type: _,
    children,
    ...props
  },
  forwardedRef,
) {
  const {
    isDisabled,
    orientation,
    position,
    setHandleElement,
    handleRef,
    transition,
    clipperRefs,
    isDragging,
    positionWithPadding,
  } = useInternalContext();
  const isVertical = orientation === "vertical";
  const ref = useComposedRefs(forwardedRef, handleRef, setHandleElement);
  // TODO: use a div (probably)

  return (
    <button
      data-replo-component-root="compare-slider"
      data-replo-part="handle"
      ref={ref}
      type="button"
      role="slider"
      aria-label="Drag to move or focus and use arrow keys"
      aria-orientation={orientation}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={position}
      disabled={isDisabled || undefined}
      {...props}
      style={{
        position: "absolute",
        top: isVertical ? `${positionWithPadding}%` : "0",
        left: isVertical ? 0 : `${positionWithPadding}%`,
        [isVertical ? "width" : "height"]: "100%",
        cursor: getCursor(isVertical, isDisabled),
        transform: isVertical
          ? `translate3d(0, -50% ,0)`
          : `translate3d(-50%, 0, 0)`,
        transition: transition
          ? `${isVertical ? "top" : "left"} ${transition}`
          : undefined,
        ...props.style,
      }}
    >
      {isFunction(children)
        ? children({
            clipperRefs,
            handleRef,
            isDisabled,
            isDragging,
            orientation,
            position,
          })
        : children}
    </button>
  );
});
// #endregion

type CompareSliderClipperProps = React.ComponentPropsWithRef<"div">;

const CompareSliderClipper = React.forwardRef<
  HTMLDivElement,
  CompareSliderClipperProps
>(function CompareSliderClipper(props, forwardedRef) {
  const { clipperRefs, orientation, transition, positionWithPadding } =
    useInternalContext();
  const { position: clipperPosition } = useCompareSliderClipperContext();
  const setClipperRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      if (clipperPosition === "before") {
        clipperRefs.current.before = element;
      } else if (clipperPosition === "after") {
        clipperRefs.current.after = element;
      } else {
        console.error(
          "Clipper position is not set. This is because you attempted to set its ref outside of a CompareSliderClipper component, or an invalid `position` was set.",
        );
      }
    },
    [clipperRefs, clipperPosition],
  );

  const isVertical = orientation === "vertical";

  // NOTE (Chance 2024-06-06): The `after` slide must always be absolute
  // positioned in order for the two sides to overlap correctly.
  const isAbsolute = clipperPosition === "after";
  const styles: React.CSSProperties = {
    userSelect: "none",
    transition: transition ? `clip-path ${transition}` : undefined,
    willChange: "clip-path, transition",
  };

  if (isAbsolute) {
    styles.position = "absolute";
    styles.inset = 0;
  } else {
    if (isVertical) {
      styles.height = "100%";
    } else {
      styles.width = "100%";
    }
  }

  if (clipperPosition === "before") {
    styles.clipPath = isVertical
      ? `inset(0 0 ${100 - positionWithPadding}% 0)`
      : `inset(0 ${100 - positionWithPadding}% 0 0)`;
  }
  if (clipperPosition === "after" && positionWithPadding) {
    styles.clipPath = isVertical
      ? `inset(${positionWithPadding}% 0 0 0)`
      : `inset(0 0 0 ${positionWithPadding}%)`;
  }

  const ref = useComposedRefs(forwardedRef, setClipperRef);
  return (
    <div
      data-replo-component-root="compare-slider"
      data-replo-part="clipper"
      ref={ref}
      {...props}
      style={{ ...styles, ...props.style }}
    />
  );
});
// #endregion

function usePrevious<ValueType>(value: ValueType): ValueType {
  const ref = React.useRef<ValueType>(value);
  // TODO (Noah, 2024-12-08): We shouldn't be using usePrevious like this,
  // we should be interrupting render when things change to re-render, but
  // leaving this here for now since adding a dependency array would break
  // the functionality
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function useLiveRef<ValueType>(value: ValueType) {
  const ref = React.useRef(value);
  React.useInsertionEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

function getCursor(isVertical: boolean, isDisabled: boolean) {
  if (isDisabled) {
    return "not-allowed";
  } else if (isVertical) {
    return "ns-resize";
  }
  return "ew-resize";
}

function calculateSliderPosition(props: {
  x: number;
  y: number;
  orientation: Orientation;
  isOffset: boolean;
  window: Window;
  rect: DOMRect;
}) {
  const isVertical = props.orientation === "vertical";
  const { isOffset, x, y, rect } = props;
  const { height, width, top, left } = rect;

  const calculatePosition = () => {
    if (isVertical) {
      return isOffset ? y - top - props.window.scrollY : y;
    }
    return isOffset ? x - left - props.window.scrollX : x;
  };
  const pixelPosition = calculatePosition();
  return Math.min(
    Math.max((pixelPosition / (isVertical ? height : width)) * 100, 0),
    100,
  );
}

function calculatePositionWithBoundsPadding(
  position: number,
  props: {
    orientation: Orientation;
    height: number;
    width: number;
    offsetHeight: number;
    offsetWidth: number;
    boundsPadding: number;
  },
) {
  const isVertical = props.orientation === "vertical";
  const { height, width, boundsPadding, offsetWidth, offsetHeight } = props;
  const zoomScale = isVertical
    ? height / (offsetHeight || 1)
    : width / (offsetWidth || 1);
  const boundsPaddingPercentage =
    ((boundsPadding * zoomScale) / (isVertical ? height : width)) * 100;
  return Math.min(
    Math.max(position, boundsPaddingPercentage * zoomScale),
    100 - boundsPaddingPercentage * zoomScale,
  );
}
